import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, HelpCircle } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import {
  fetchProjectConfig,
  type ProjectConfig,
} from '@/features/settings/api/project-config'

interface ToggleRowProps {
  label: string
  description: string
  checked: boolean
  onChange: (v: boolean) => void
}

function ToggleRow({ label, description, checked, onChange }: ToggleRowProps) {
  const id = label.replace(/\s/g, '_')
  return (
    <div className="flex items-start gap-4 py-4 border-b border-border last:border-0">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onChange(Boolean(v))}
        className="mt-0.5"
      />
      <div className="flex-1">
        <label htmlFor={id} className="text-sm font-medium text-foreground cursor-pointer">
          {label}
        </label>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
  )
}

export function PermissionsContent() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)

  const [config, setConfig] = useState<ProjectConfig>({})

  const { data, isLoading } = useQuery({
    queryKey: ['project-config', projectId],
    queryFn: () => fetchProjectConfig(projectId!),
    enabled: !!projectId,
  })

  useEffect(() => {
    if (data) setConfig(data)
  }, [data])

  const saveMutation = useMutation({
    mutationFn: () =>
      supabase
        .from('projects')
        .update({ config: config, updated_by: user?.id })
        .eq('id', projectId!)
        .then(({ error }) => { if (error) throw error }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-config', projectId] })
    },
  })

  const set = (key: keyof ProjectConfig, value: boolean | string) =>
    setConfig((prev) => ({ ...prev, [key]: value }))

  // The DB default for a missing key is ["admin"]; admin is always implied and
  // not toggleable in the UI.
  const deleteRoles = config.delete_record_roles ?? ['admin']
  const toggleDeleteRole = (role: 'data_entry' | 'approver', on: boolean) =>
    setConfig((prev) => {
      const current = prev.delete_record_roles ?? ['admin']
      const withoutRole = current.filter((r) => r !== role)
      return {
        ...prev,
        delete_record_roles: on ? [...withoutRole, role] : withoutRole,
      }
    })

  // review_mode goes through a guarded RPC (refuses to switch to 'none' while
  // records sit in review limbo), NOT the generic config save.
  const reviewModeMutation = useMutation({
    mutationFn: async (mode: 'required' | 'none') => {
      const { data, error } = await supabase.rpc('set_project_review_mode', {
        p_project_id: projectId!,
        p_mode: mode,
      })
      if (error) throw error
      const r = data as { success?: boolean; error?: string; count?: number }
      if (r?.error === 'pending_review_records')
        throw new Error(`${r.count ?? ''} سابقه در حال بررسی وجود دارد. ابتدا آن‌ها را تعیین‌تکلیف کنید.`)
      if (r?.error === 'forbidden') throw new Error('فقط مدیر پروژه می‌تواند حالت بازبینی را تغییر دهد')
      if (r?.error) throw new Error(r.error)
      return mode
    },
    onSuccess: (mode) => {
      setConfig((prev) => ({ ...prev, review_mode: mode }))
      qc.invalidateQueries({ queryKey: ['project-config', projectId] })
    },
  })

  const reviewMode = config.review_mode ?? 'required'

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-14 bg-muted rounded animate-pulse" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-sm font-semibold text-foreground mb-1">گردش کار بازبینی</h2>
        <p className="text-xs text-muted-foreground mb-4">
          آیا سوابق پیش از نهایی‌شدن باید توسط ناظر تأیید شوند؟
        </p>
        <div className="bg-card border border-border rounded-xl p-5 space-y-3">
          {(['required','none'] as const).map((mode) => (
            <label
              key={mode}
              className="flex items-start gap-3 cursor-pointer"
            >
              <input
                type="radio"
                name="review_mode"
                checked={reviewMode === mode}
                disabled={reviewModeMutation.isPending}
                onChange={() => {
                  if (reviewMode === mode) return
                  reviewModeMutation.mutate(mode, {
                    onError: (err) =>
                      alert((err as Error).message),
                  })
                }}
                className="mt-1"
              />
              <div>
                <p className="text-sm font-medium text-foreground">
                  {mode === 'required' ? 'بازبینی الزامی' : 'بدون بازبینی'}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {mode === 'required'
                    ? 'سوابق پس از ارسال نهایی برای تأیید به ناظر می‌روند و قابل رد/تأیید هستند.'
                    : 'سوابق با ارسال نهایی مستقیماً تأیید می‌شوند؛ مرحله ناظر وجود ندارد.'}
                </p>
              </div>
            </label>
          ))}
          {reviewMode === 'none' && (
            <p className="text-[11px] text-yellow-500/90 bg-yellow-500/10 rounded-md px-3 py-2">
              در این حالت نقش «ناظر» در این پروژه کاربردی ندارد.
            </p>
          )}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-foreground mb-1">سوابق</h2>
        <p className="text-xs text-muted-foreground mb-4">تنظیمات مربوط به دسترسی و مدیریت سوابق</p>
        <div className="bg-card border border-border rounded-xl px-5">
          <ToggleRow
            label="اختصاص سابقه به کاربر"
            description="امکان تعیین مالک برای هر سابقه. اگر فعال باشد، سابقه به کاربر ایجادکننده اختصاص می‌یابد."
            checked={config.allow_record_assignment ?? false}
            onChange={(v) => set('allow_record_assignment', v)}
          />
          <ToggleRow
            label="ویرایش سوابق دیگران"
            description="کارشناسان ورود داده می‌توانند سوابق ایجادشده توسط دیگران را ویرایش کنند."
            checked={config.allow_edit_others_records ?? false}
            onChange={(v) => set('allow_edit_others_records', v)}
          />
          <ToggleRow
            label="ویرایش پس از تأیید"
            description="با ویرایش یک سابقه تأییدشده، سابقه دوباره باز می‌شود (به «در حال تکمیل» برمی‌گردد). در حالت بازبینی الزامی، باید مجدداً تأیید شود."
            checked={config.edit_after_approval ?? false}
            onChange={(v) => set('edit_after_approval', v)}
          />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-foreground mb-1">حذف سوابق</h2>
        <p className="text-xs text-muted-foreground mb-4">
          چه کسانی می‌توانند سوابق را حذف نرم کنند و تا کجا. حذف همیشه نرم است و مدیر می‌تواند آن را بازیابی کند.
        </p>
        <div className="bg-card border border-border rounded-xl px-5">
          <div className="py-4 border-b border-border">
            <p className="text-sm font-medium text-foreground mb-0.5">نقش‌های مجاز به حذف</p>
            <p className="text-xs text-muted-foreground mb-3">
              مدیر پروژه همیشه مجاز است. سایر نقش‌ها فقط در صورت انتخاب می‌توانند حذف کنند.
            </p>
            <div className="flex flex-col gap-2.5">
              <label className="flex items-center gap-2.5 text-sm opacity-60">
                <Checkbox checked disabled />
                مدیر پروژه (همیشه)
              </label>
              {(
                [
                  { value: 'data_entry', label: 'کارشناس ورود داده' },
                  { value: 'approver', label: 'ناظر' },
                ] as const
              ).map((r) => (
                <label key={r.value} className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <Checkbox
                    checked={deleteRoles.includes(r.value)}
                    onCheckedChange={(v) => toggleDeleteRole(r.value, Boolean(v))}
                  />
                  {r.label}
                </label>
              ))}
            </div>
          </div>
          <ToggleRow
            label="حذف سوابق ارسال‌شده"
            description="نقش‌های مجاز (به‌جز مدیر که همیشه می‌تواند) علاوه بر سوابق ارسال‌نشده (پیش‌نویس، در جریان، نیاز به اصلاح)، سوابق ارسال‌شده و تأییدشده را هم می‌توانند حذف کنند."
            checked={config.allow_delete_submitted_records ?? false}
            onChange={(v) => set('allow_delete_submitted_records', v)}
          />
          <ToggleRow
            label="حذف سوابق دیگران"
            description="نقش‌های مجاز می‌توانند سوابقی را که خودشان ایجاد نکرده‌اند یا مالک آن نیستند نیز حذف کنند. در غیر این صورت فقط سوابق خودشان."
            checked={config.allow_delete_others_records ?? false}
            onChange={(v) => set('allow_delete_others_records', v)}
          />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-foreground mb-1">نظرات</h2>
        <p className="text-xs text-muted-foreground mb-4">تنظیمات مربوط به ثبت و مدیریت نظرات</p>
        <div className="bg-card border border-border rounded-xl px-5">
          <ToggleRow
            label="همه کاربران می‌توانند نظر اضافه کنند"
            description="اگر غیرفعال باشد، فقط ناظران می‌توانند نظر ثبت کنند."
            checked={config.allow_comment_add_all ?? true}
            onChange={(v) => set('allow_comment_add_all', v)}
          />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-foreground mb-1">خروجی داده</h2>
        <p className="text-xs text-muted-foreground mb-4">تنظیمات خروجی گرفتن از داده‌های پروژه</p>
        <div className="bg-card border border-border rounded-xl px-5">
          <ToggleRow
            label="امکان خروجی گرفتن"
            description="فعال‌سازی امکان خروجی داده برای ناظران و ادمین."
            checked={config.export_enabled ?? true}
            onChange={(v) => set('export_enabled', v)}
          />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-foreground mb-1">لینک‌های عمومی فرم</h2>
        <p className="text-xs text-muted-foreground mb-4">جمع‌آوری داده از طریق لینک اشتراکی بدون نیاز به ورود کاربر</p>
        <div className="bg-card border border-border rounded-xl px-5">
          <ToggleRow
            label="فعال‌سازی لینک‌های عمومی"
            description="امکان ساخت لینک اشتراکی برای تکمیل فرم‌های منتشرشده توسط افراد بدون حساب کاربری. لینک‌ها در تب «لینک‌های عمومی» مدیریت می‌شوند."
            checked={config.public_links_enabled ?? false}
            onChange={(v) => set('public_links_enabled', v)}
          />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-foreground mb-1">شناسه سابقه</h2>
        <p className="text-xs text-muted-foreground mb-4">پیشوند نمایشی برای شناسه سوابق این پروژه</p>
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              پیشوند شناسه
              <HelpCircle className="w-3.5 h-3.5 text-muted-foreground" />
            </Label>
            <Input
              value={config.record_id_prefix ?? ''}
              onChange={(e) => set('record_id_prefix', e.target.value)}
              placeholder="مثال: P"
              dir="ltr"
              className="w-40"
            />
          </div>
        </div>
      </section>

      <Button
        onClick={() => saveMutation.mutate()}
        disabled={saveMutation.isPending}
        className="gap-1.5"
      >
        <Save className="w-4 h-4" />
        {saveMutation.isPending ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
      </Button>
    </div>
  )
}

export function PermissionsPage() {
  return (
    <div className="flex flex-col h-full">
      <PageHeader title="دسترسی‌ها" breadcrumbs={[{ label: 'دسترسی‌ها' }]} />
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl">
          <PermissionsContent />
        </div>
      </div>
    </div>
  )
}
